/**
 * Automatic background retry for persisted recordings (CLA-945 Phase 2).
 *
 * CLA-938 Phase 1 stopped web losing consult audio, but recovery stayed
 * MANUAL: an orphan was detected on load and *offered*, never retried. That was
 * the deliberate safe default — an unattended retry storm at login is exactly
 * what a degraded API does not need — but it left web the weakest of the three
 * clients. iOS retries for ~100 minutes unattended, and Android now does ~95
 * (CLA-963). On web the audio was safe but delivery waited for a human.
 *
 * This closes that gap while keeping the reason for the original caution:
 *
 *  - **Backoff, not a loop.** Delays climb 30s → 60s → 120s → … capped at 10
 *    minutes, so a degraded API sees a handful of requests over an hour rather
 *    than a tab hammering it.
 *  - **One tab retries, not all of them.** See the lock below.
 *  - **Only while the tab is open**, and paused while hidden. This is a
 *    best-effort improvement to recovery *speed*; the durable guarantee is
 *    still the IndexedDB row, which survives everything.
 *
 * Deliberately NOT here: the iOS "delete only on proof of a transcribed draft"
 * rule, and the progressive-fallback fix. Both are separate items on CLA-945.
 */

/** First backoff step. Matches the Android worker's base (CLA-963). */
export const BASE_DELAY_MS = 30_000

/**
 * Ceiling on a single wait. A budget made of enormous gaps leaves an upload
 * idle long after the API recovers — the same reasoning that moved Android off
 * exponential backoff.
 */
export const MAX_DELAY_MS = 10 * 60_000

/**
 * Attempts before this scheduler stops trying automatically.
 *
 * The row is NOT deleted at that point and the manual retry button stays — the
 * clinician keeps every option they had before. This only bounds the
 * *unattended* effort, so a permanently rejected upload (a 4xx that will never
 * succeed) cannot retry forever in a tab someone left open.
 */
export const MAX_AUTO_ATTEMPTS = 8

/**
 * Delay before the next automatic attempt, given how many have already failed.
 *
 * Exponential from 30s but capped, so the sequence is
 * 30s, 60s, 120s, 240s, 480s, 600s, 600s, 600s — about 46 minutes of
 * unattended effort across MAX_AUTO_ATTEMPTS.
 */
export function nextDelayMs(attempts: number): number {
  if (attempts <= 0) return BASE_DELAY_MS
  return Math.min(BASE_DELAY_MS * 2 ** attempts, MAX_DELAY_MS)
}

/** Total unattended window, for asserting the budget in tests. */
export function totalWindowMs(maxAttempts: number = MAX_AUTO_ATTEMPTS): number {
  let total = 0
  for (let i = 0; i < maxAttempts; i++) total += nextDelayMs(i)
  return total
}

/**
 * Whether a row should be retried automatically right now.
 *
 * `plan_capped` is excluded deliberately: it is a 402 that retrying cannot
 * fix, and hammering it would turn a billing prompt into a request storm.
 * `uploading` is excluded because something already has it in flight.
 */
export function shouldAutoRetry(
  row: { status: string; attempts: number; updatedAt: string },
  now: number,
): boolean {
  if (row.status !== 'pending') return false
  if (row.attempts >= MAX_AUTO_ATTEMPTS) return false
  // `updatedAt` stands in for "last attempted". There is no dedicated
  // timestamp, but every attempt goes through `updatePendingStatus`, which
  // stamps it — so adding a column would duplicate what the row already knows.
  const last = Date.parse(row.updatedAt)
  // An unparseable timestamp must not mean "retry immediately, forever".
  if (Number.isNaN(last)) return false
  return now - last >= nextDelayMs(row.attempts)
}

type SchedulerDeps = {
  /** Rows currently pending, newest projection. */
  list: () => Promise<Array<{ id?: number; status: string; attempts: number; updatedAt: string }>>
  /** The existing user-initiated retry — reused so both paths share one code path. */
  retry: (id: number) => Promise<void>
  /** Injectable for tests. */
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
}

/**
 * Cross-tab exclusion.
 *
 * Auto-retry is what makes this necessary. With a manual button, two tabs
 * colliding needed a human to click twice in both; unattended, every open tab
 * would wake on the same schedule and upload the same multi-megabyte blob at
 * the same moment. The shared Idempotency-Key keeps that SAFE — the server
 * dedups onto one note — but it is pure waste on exactly the degraded
 * connection that caused the failure.
 *
 * `navigator.locks` is the right primitive and is held only for the duration of
 * one sweep. Where it is unavailable the scheduler still runs: correctness does
 * not depend on the lock, only efficiency.
 */
async function withTabLock<T>(fn: () => Promise<T>): Promise<T | undefined> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks
  if (!locks?.request) return fn()
  return locks.request(
    'claro:pending-upload-retry',
    { ifAvailable: true },
    async (lock) => {
      // Another tab is mid-sweep. Skip this tick rather than queueing behind
      // it — by the time the lock frees, its work has already been done.
      if (!lock) return undefined
      return fn()
    },
  )
}

export class UploadRetryScheduler {
  private handle: unknown = null
  private running = false
  private stopped = true

  // Explicit field + assignment, not a parameter property: this repo enables
  // `erasableSyntaxOnly`, so TS-only syntax that emits runtime code is banned.
  private deps: SchedulerDeps

  constructor(deps: SchedulerDeps) {
    this.deps = deps
  }

  private get now() {
    return this.deps.now ?? (() => Date.now())
  }

  start() {
    if (!this.stopped) return
    this.stopped = false
    this.schedule(BASE_DELAY_MS)
  }

  stop() {
    this.stopped = true
    if (this.handle != null) {
      ;(this.deps.clearTimer ?? clearTimeout)(this.handle as never)
      this.handle = null
    }
  }

  private schedule(ms: number) {
    if (this.stopped) return
    if (this.handle != null) (this.deps.clearTimer ?? clearTimeout)(this.handle as never)
    this.handle = (this.deps.setTimer ?? setTimeout)(() => {
      void this.tick()
    }, ms)
  }

  /**
   * Run one sweep. Public so `online` can drive it immediately — a recovered
   * connection is the single best moment to retry, and waiting out a backoff
   * that was scheduled while offline wastes the recovery.
   */
  async tick(): Promise<void> {
    if (this.stopped || this.running) return
    this.running = true
    try {
      await withTabLock(async () => {
        const rows = await this.deps.list()
        const now = this.now()
        for (const row of rows) {
          if (this.stopped) break
          if (row.id == null) continue
          if (!shouldAutoRetry(row, now)) continue
          // Sequential on purpose: these are multi-megabyte uploads on a link
          // that has already failed at least once.
          await this.deps.retry(row.id)
        }
      })
    } catch {
      // A sweep must never throw into a timer callback. The row survives and
      // the next tick will try again.
    } finally {
      this.running = false
      this.schedule(BASE_DELAY_MS)
    }
  }
}
